import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Circle, CircleCheck, Eye, EyeOff, KeyRound } from "lucide-react";
import { Ic } from "./icons";
import { Btn, Field, useObId } from "./primitives";
import { PW_MIN_LENGTH, pwCheck } from "./password";

const STRENGTH_COLORS = ["#DC2626", "#DC2626", "#D97706", "#2B5BF5", "#059669"] as const;

export interface PasswordCardProps {
  /** The new password. */
  value: string;
  onChange: (next: string) => void;
  /** The confirmation field. */
  confirm: string;
  onConfirmChange: (next: string) => void;
  /** The read-only sign-in address shown at the top of the card. */
  email: string;
  /** Lower-case parts the password must not contain (see `pwNameParts`). */
  nameParts: ReadonlyArray<string>;
  /** Card heading; defaults to "Set your password". */
  title?: React.ReactNode;
  /** "Step N" eyebrow; pass null to hide it. Defaults to 1. */
  step?: number | null;
  /** Minimum length from the invitation's password_policy (default 12). */
  minLength?: number;
  /** Server-side password errors (accept's `{password: [...]}`), shown under the password field. */
  serverErrors?: ReadonlyArray<string> | null;
  /** Submit on Enter in either field. */
  onSubmit?: () => void;
}

/** The design's PasswordCard (ob-entry.jsx): password + live rules + strength meter + confirm. */
export function PasswordCard({
  value,
  onChange,
  confirm,
  onConfirmChange,
  email,
  nameParts,
  title,
  step = 1,
  minLength = PW_MIN_LENGTH,
  serverErrors,
  onSubmit,
}: PasswordCardProps) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const id = useObId("pw");
  const c = pwCheck(value, nameParts, minLength);
  const labels = [
    t("onboarding.kit.password.strength.tooWeak", "Too weak"),
    t("onboarding.kit.password.strength.weak", "Weak"),
    t("onboarding.kit.password.strength.fair", "Fair"),
    t("onboarding.kit.password.strength.good", "Good"),
    t("onboarding.kit.password.strength.strong", "Strong"),
  ];
  const mismatch = confirm.length > 0 && confirm !== value;
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };
  const rule = (ok: boolean, text: string, key: string) => (
    <li
      key={key}
      data-ok={ok}
      style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: ok ? "#065F46" : "var(--mu)" }}
    >
      <Ic icon={ok ? CircleCheck : Circle} weight={ok ? "bold" : "regular"} size={16} color={ok ? "#059669" : "#64748B"} />
      {text}
      <span className="ob-sr">
        {ok ? t("onboarding.kit.password.ruleMet", "Met") : t("onboarding.kit.password.ruleNotMet", "Not met")}
      </span>
    </li>
  );
  const serverErr = serverErrors && serverErrors.length > 0 ? serverErrors.join(" ") : null;

  return (
    <section className="ob-glass" style={{ padding: "clamp(20px,3cqi,28px)", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span className="ob-tile">
          <Ic icon={KeyRound} />
        </span>
        <div>
          {step != null && (
            <p className="ob-eye">{t("onboarding.kit.step", { defaultValue: "Step {{n}}", n: step })}</p>
          )}
          <h2 className="ob-h3">{title ?? t("onboarding.kit.password.title", "Set your password")}</h2>
        </div>
      </div>
      <Field label={t("onboarding.kit.password.signInAddress", "Sign-in address")}>
        <input className="ob-in" readOnly value={email} dir="ltr" autoComplete="username" />
      </Field>
      <Field label={t("onboarding.kit.password.password", "Password")} err={serverErr}>
        <span style={{ position: "relative", display: "block" }}>
          <input
            id={id}
            className="ob-in"
            type={show ? "text" : "password"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="new-password"
            dir="ltr"
            style={{ paddingInlineEnd: 48 }}
            aria-describedby={`${id}-rules`}
            aria-invalid={serverErr ? true : undefined}
          />
          <Btn
            kind="ter"
            size="s"
            icon={show ? EyeOff : Eye}
            style={{ position: "absolute", top: 6, insetInlineEnd: 6, background: "transparent" }}
            aria-label={
              show ? t("onboarding.kit.password.hide", "Hide password") : t("onboarding.kit.password.show", "Show password")
            }
            aria-pressed={show}
            onClick={(e) => {
              e.preventDefault();
              setShow(!show);
            }}
          />
        </span>
      </Field>
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4 }} aria-hidden="true">
          {[1, 2, 3, 4].map((i) => (
            <span
              key={i}
              style={{
                height: 6,
                borderRadius: 3,
                background: value && c.score >= i ? STRENGTH_COLORS[c.score] : "rgba(148,163,184,.3)",
                transition: "background .2s",
              }}
            />
          ))}
        </div>
        <p className="ob-sm" style={{ marginTop: 6 }} aria-live="polite">
          {t("onboarding.kit.password.strengthLabel", "Strength:")}{" "}
          <strong style={{ color: value ? STRENGTH_COLORS[c.score] : "var(--mu)" }} data-testid="pw-strength">
            {value ? labels[c.score] : "—"}
          </strong>
        </p>
      </div>
      <ul id={`${id}-rules`} style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {rule(c.len, t("onboarding.kit.password.ruleLength", { defaultValue: "{{n}} or more characters", n: minLength }), "len")}
        {rule(c.breach, t("onboarding.kit.password.ruleBreach", "Not in known breach lists"), "breach")}
        {rule(c.name, t("onboarding.kit.password.ruleName", "Doesn't contain your name or email"), "name")}
      </ul>
      <Field
        label={t("onboarding.kit.password.confirm", "Confirm password")}
        err={mismatch ? t("onboarding.kit.password.mismatch", "Passwords don't match.") : null}
      >
        <input
          className="ob-in"
          type={show ? "text" : "password"}
          value={confirm}
          onChange={(e) => onConfirmChange(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="new-password"
          dir="ltr"
          aria-invalid={mismatch}
        />
      </Field>
    </section>
  );
}

/** True when the PasswordCard's state is submittable (rules pass and both fields match). */
export function passwordReady(value: string, confirm: string, nameParts: ReadonlyArray<string>, minLength = PW_MIN_LENGTH): boolean {
  return pwCheck(value, nameParts, minLength).ok && value === confirm;
}
